import { createSignal, onMount } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import type { FilterValue } from './types';
import type { Accessor, Setter } from 'solid-js';

export enum FilterConfigKey {
  Portfolio = 'portfolio',
  Property = 'property',
  // more type here
}

export type FilterConfig = {
  label?: string;
  key?: string;
  options?: any[];
  multiple?: boolean;
  value?: Accessor<any>;
  onChange?: Setter<any>;
};

export type FilterOwner<T extends FilterValue = FilterValue> = ReturnType<typeof useFilters<T>>;

export type FiltersOpts<T extends FilterValue = FilterValue> = { defaultValue?: Partial<T> };

export const useFilters = <T extends FilterValue>(opts?: FiltersOpts<T>) => {
  const defaultValue = () => (opts?.defaultValue ?? {}) as T;
  const [filterValue, setFilterValue] = createSignal<T>(defaultValue());

  const [config, _setConfig] = createStore<Partial<Record<FilterConfigKey, FilterConfig>>>();

  const setConfig = (newConfig: Partial<Record<FilterConfigKey, FilterConfig>>) => {
    _setConfig(
      produce((draft) => {
        Object.entries(newConfig).forEach(([key, value]) => {
          const kk1 = key as FilterConfigKey;
          if (!draft[kk1]) draft[kk1] = {};
          Object.assign(draft[kk1], value);
        });
      })
    );
  };

  const handleFilterReset = () => {
    setFilterValue(() => defaultValue());
  };

  const parsers: ((value: T) => any)[] = [];

  const useParseFilter = (parser: (value: T) => any) => {
    parsers.push(parser);
  };

  const parseFilter = (value: T) => {
    return parsers.reduce((acc, parser) => parser(acc), Object.assign({}, value));
  };

  const getConfigByKey = (key: FilterConfigKey) => {
    return config[key];
  };

  onMount(() => {
    if (opts?.defaultValue) {
      setFilterValue((prev) => ({ ...prev, ...defaultValue() }));
    }
  });

  return {
    filterValue,
    setFilterValue,
    config,
    setConfig,
    handleFilterReset,
    parseFilter,
    useParseFilter,
    getConfigByKey,
  };
};
